// Fetch wrappers for the Trash takeover view.
//
// apiListDeleted  — GET /app/api/trash (server-side filtering, B7a).
// apiRestoreOne   — POST /app/api/restore (single path + sha256).
// apiRestoreMany  — sequential loop over items; never aborts on failure.
//
// Contract drift from the plan: /app/api/restore requires `{ path, version }`
// where `version` is the sha256 hex string of the version to restore. We
// resolve this from each DeletedFile's `previous_sha256` (the writable
// predecessor recorded by list_deleted). Items whose `previous_sha256` is
// null (file created then immediately deleted) cannot be restored — we
// surface that as a NO_PREVIOUS_VERSION failure rather than calling the API.

const q = (orgSlug, wsHandle) =>
  '?org=' + encodeURIComponent(orgSlug) +
  '&workspace=' + encodeURIComponent(wsHandle);

function isPresent(v) {
  if (v == null) return false;
  if (typeof v === 'string' && v === '') return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

export async function apiListDeleted(orgSlug, wsHandle, filters = {}) {
  let url = '/app/api/trash' + q(orgSlug, wsHandle);
  // Security note: deleted_by values are assumed comma-free (typical for
  // email addresses and `mxs_…` tokens). If exotic identifiers with embedded
  // commas become possible, switch to repeated &deleted_by=... params with
  // array decoding on the server.
  const append = (key, value) => {
    url += '&' + key + '=' + encodeURIComponent(String(value));
  };
  if (isPresent(filters.pathPrefix)) append('path_prefix', filters.pathPrefix);
  if (isPresent(filters.deletedBy)) {
    const joined = Array.isArray(filters.deletedBy)
      ? filters.deletedBy.join(',')
      : filters.deletedBy;
    if (joined !== '') append('deleted_by', joined);
  }
  if (isPresent(filters.deletedAfter)) append('deleted_after', filters.deletedAfter);
  if (isPresent(filters.deletedBefore)) append('deleted_before', filters.deletedBefore);
  if (isPresent(filters.remainingDaysLte)) append('remaining_days_lte', filters.remainingDaysLte);
  if (isPresent(filters.headLimit)) append('head_limit', filters.headLimit);
  if (isPresent(filters.offset)) append('offset', filters.offset);

  const r = await fetch(url);
  if (!r.ok) throw new Error('/app/api/trash ' + r.status);
  return await r.json();
}

export async function apiRestoreOne(orgSlug, wsHandle, path, version) {
  const url = '/app/api/restore' + q(orgSlug, wsHandle);
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, version }),
  });
  if (r.status === 404) {
    const err = new Error('/app/api/restore 404');
    err.code = 'NOT_FOUND';
    throw err;
  }
  if (!r.ok) throw new Error('/app/api/restore ' + r.status);
  return await r.json();
}

export async function apiRestoreMany(orgSlug, wsHandle, items) {
  const restored = [];
  const failed = [];
  for (const item of items) {
    if (!item.previous_sha256) {
      failed.push({
        path: item.path,
        code: 'NO_PREVIOUS_VERSION',
        message: 'no writable predecessor version recorded',
      });
      continue;
    }
    try {
      await apiRestoreOne(orgSlug, wsHandle, item.path, item.previous_sha256);
      restored.push(item.path);
    } catch (e) {
      failed.push({
        path: item.path,
        message: e.message,
        ...(e.code ? { code: e.code } : {}),
      });
    }
  }
  return { restored, failed };
}
